import { empty } from "@uri/gamla";
import { useCallback, useEffect, useState } from "preact/hooks";
import { ClipLoader } from "react-spinners";
import { useAuth } from "./db.ts";
import { ErrorMessage, Loading } from "./uiComponents.tsx";
import { t } from "./theme.ts";
import { getApiBase } from "./config.ts";
import "./style.css";

const apiBase = getApiBase();

const callApi = (endpoint: string, payload: Record<string, unknown>) =>
  fetch(apiBase, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ endpoint, payload }),
  }).then((r) => r.json());

type UserTokenRecord = {
  id: string;
  name: string;
  createdAt: number;
  maskedToken: string;
};

const copyToClipboard = (text: string) => navigator.clipboard.writeText(text);

const UserTokenRow = (
  { token, onDelete }: {
    token: UserTokenRecord;
    onDelete: (id: string) => void;
  },
) => {
  const th = t();
  return (
    <li
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 0",
        borderBottom: `1px solid ${th.border}`,
      }}
    >
      <span style={{ fontWeight: 500, color: th.text, minWidth: 80 }}>
        {token.name}
      </span>
      <span
        style={{
          color: th.textFaint,
          fontSize: 12,
          fontFamily: "monospace",
        }}
      >
        {token.maskedToken}
      </span>
      <span
        style={{ color: th.textFaint, fontSize: 12, flex: 1, textAlign: "end" }}
      >
        {new Date(token.createdAt).toLocaleDateString()}
      </span>
      <button
        type="button"
        className="compact-button"
        style={{ fontSize: 12, color: th.danger }}
        onClick={() => onDelete(token.id)}
      >
        Delete
      </button>
    </li>
  );
};

const NewTokenCallout = (
  { token, onDismiss }: { token: string; onDismiss: () => void },
) => {
  const th = t();
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    copyToClipboard(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div
      style={{
        border: `1px solid ${th.border}`,
        borderRadius: 6,
        padding: 12,
        marginBottom: 16,
        background: th.cardBg,
      }}
    >
      <div
        style={{
          color: th.text,
          fontSize: 13,
          fontWeight: 500,
          marginBottom: 6,
        }}
      >
        Copy your new token now — it won't be shown again.
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <code
          style={{
            flex: 1,
            fontSize: 12,
            color: th.text,
            background: th.pageBg,
            padding: "6px 8px",
            borderRadius: 4,
            overflowX: "auto",
            whiteSpace: "nowrap",
          }}
        >
          {token}
        </code>
        <button type="button" className="compact-button" onClick={handleCopy}>
          {copied ? "Copied" : "Copy"}
        </button>
        <button type="button" className="compact-button" onClick={onDismiss}>
          Done
        </button>
      </div>
    </div>
  );
};

export const UserTokenManager = (
  { sessionToken }: { sessionToken: string },
) => {
  const th = t();
  const [tokens, setTokens] = useState<UserTokenRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [newTokenName, setNewTokenName] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [newToken, setNewToken] = useState("");
  const [error, setError] = useState("");

  const refreshTokens = useCallback(() => {
    setLoading(true);
    setError("");
    callApi("listUserTokens", { sessionToken })
      .then(
        (res: { ok?: boolean; tokens?: UserTokenRecord[]; error?: string }) => {
          if (res.ok && res.tokens) setTokens(res.tokens);
          else setError(res.error || "Failed to load tokens");
        },
      )
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [sessionToken]);

  useEffect(refreshTokens, [refreshTokens]);

  const handleCreate = () => {
    if (!newTokenName.trim()) return;
    callApi("createUserToken", { sessionToken, name: newTokenName.trim() })
      .then((res: { ok?: boolean; token?: string; error?: string }) => {
        if (res.ok && res.token) {
          setNewToken(res.token);
          setNewTokenName("");
          setShowCreate(false);
          refreshTokens();
        } else setError(res.error || "Failed to create token");
      })
      .catch((e: Error) => setError(e.message));
  };

  const handleDelete = (tokenId: string) => {
    callApi("deleteUserToken", { sessionToken, tokenId })
      .then((res: { ok?: boolean; error?: string }) => {
        if (res.ok) refreshTokens();
        else setError(res.error || "Failed to delete token");
      })
      .catch((e: Error) => setError(e.message));
  };

  return (
    <div>
      <h2 style={{ color: th.text, fontSize: 18, marginBottom: 4 }}>
        Personal access tokens
      </h2>
      <p style={{ color: th.textFaint, fontSize: 14, marginBottom: 20 }}>
        Read-only API tokens scoped to the conversations you can view. Use them
        with the ViewChat API to fetch your conversations and messages.
      </p>
      <div
        style={{
          fontWeight: 500,
          fontSize: 14,
          color: th.textDim,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          marginBottom: 8,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        Tokens
        <button
          type="button"
          className="compact-button"
          style={{ fontSize: 12, textTransform: "none" }}
          onClick={() => setShowCreate((v) => !v)}
        >
          {showCreate ? "Cancel" : "+ New"}
        </button>
      </div>
      {showCreate && (
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <input
            type="text"
            placeholder="Token name (e.g. my-script)"
            value={newTokenName}
            onChange={(e) =>
              setNewTokenName((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            className="input-field"
            style={{ flex: 1, marginBottom: 0 }}
          />
          <button
            type="button"
            className="compact-button"
            onClick={handleCreate}
          >
            Create
          </button>
        </div>
      )}
      {newToken && (
        <NewTokenCallout
          token={newToken}
          onDismiss={() => setNewToken("")}
        />
      )}
      {error && (
        <div style={{ color: th.danger, fontSize: 13, marginBottom: 8 }}>
          {error}
        </div>
      )}
      {loading
        ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "10px 0",
            }}
          >
            <ClipLoader size={20} color={th.textFaint} />
          </div>
        )
        : empty(tokens)
        ? (
          <div style={{ color: th.textFaint, fontSize: 13 }}>
            No tokens yet. Create one to get started.
          </div>
        )
        : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {tokens.map((tok) => (
              <UserTokenRow key={tok.id} token={tok} onDelete={handleDelete} />
            ))}
          </ul>
        )}
    </div>
  );
};

export const SettingsPage = () => {
  const th = t();
  const { isLoading, user, error } = useAuth();
  if (isLoading) return <Loading text="Loading..." />;
  if (error) return <ErrorMessage error={error} />;
  if (!user) {
    return (
      <ErrorMessage
        error={new Error("You must be signed in to view settings.")}
      />
    );
  }
  return (
    <div style={{ maxWidth: 640, margin: "0 auto", padding: "24px 16px" }}>
      <h1 style={{ color: th.text, fontSize: 22, marginBottom: 20 }}>
        Settings
      </h1>
      <div style={{ marginBottom: 28 }}>
        <div
          style={{
            fontWeight: 500,
            fontSize: 14,
            color: th.textDim,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            marginBottom: 8,
          }}
        >
          Signed in as
        </div>
        <div style={{ color: th.text, fontSize: 15 }}>{user.email}</div>
      </div>
      <UserTokenManager sessionToken={user.refresh_token} />
    </div>
  );
};
